import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import logger from './utils/logger';

function ReserveFundsManagement() {
  const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [companyOptions, setCompanyOptions] = useState([]);
  const [company, setCompany] = useState('');
  const [direction, setDirection] = useState('in');
  const [amount, setAmount] = useState('');
  const [occurredDate, setOccurredDate] = useState(getTodayDate());
  const [note, setNote] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listLimit] = useState(20);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [totals, setTotals] = useState({ total_in: 0, total_out: 0, net: 0 });
  const [listError, setListError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatNumber = (value) => new Intl.NumberFormat('ko-KR').format(Math.floor(value || 0));
  const formatCurrency = (value) => `${formatNumber(value)}원`;

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/companies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyOptions(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      logger.apiError('GET', '/api/companies', err);
      setError('분류 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (filterCompany) params.set('company', filterCompany);
    if (filterDirection) params.set('direction', filterDirection);
    // 기간 필터 — 지정하지 않으면 합계가 전체 기간 기준이 된다.
    if (filterDateFrom) params.set('dateFrom', filterDateFrom);
    if (filterDateTo) params.set('dateTo', filterDateTo);
    params.set('page', String(listPage));
    params.set('limit', String(listLimit));
    return params.toString();
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/reserve-funds?${buildQuery()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotals(response.data?.totals || { total_in: 0, total_out: 0, net: 0 });
      setListTotalPages(response.data?.pagination?.totalPages || 0);
      setListTotal(response.data?.pagination?.total || 0);
      setListError('');
    } catch (err) {
      logger.apiError('GET', '/api/reserve-funds', err);
      setListError('유보금 내역을 불러오지 못했습니다.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchList();
  }, [listPage, filterCompany, filterDirection, filterDateFrom, filterDateTo]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!company) {
      setError('분류를 선택해주세요.');
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) {
      setError('금액은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post('/api/reserve-funds', {
        company,
        direction,
        amount: value,
        occurred_date: occurredDate,
        note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('유보금 내역이 저장되었습니다.');
      setCompany('');
      setDirection('in');
      setAmount('');
      setOccurredDate(getTodayDate());
      setNote('');
      await fetchList();
      setIsModalOpen(false);
    } catch (err) {
      logger.apiError('POST', '/api/reserve-funds', err);
      setError(err?.response?.data?.error || '유보금 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingRow({
      id: item.id,
      company: item.company,
      direction: item.direction,
      amount: String(item.amount ?? ''),
      occurredDate: item.occurred_date,
      note: item.note || ''
    });
    setError('');
    setSuccess('');
  };

  const handleEditChange = (field, value) => {
    if (field === 'company') {
      return;
    }
    setEditingRow((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    setError('');
    setSuccess('');

    const value = Number(editingRow.amount);
    if (!Number.isFinite(value) || value < 0) {
      setError('금액은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/reserve-funds/${editingRow.id}`, {
        company: editingRow.company,
        direction: editingRow.direction,
        amount: value,
        occurred_date: editingRow.occurredDate,
        note: editingRow.note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('유보금 내역이 수정되었습니다.');
      setEditingRow(null);
      await fetchList();
    } catch (err) {
      logger.apiError('PUT', '/api/reserve-funds/:id', err);
      setError(err?.response?.data?.error || '유보금 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
  };

  const handleDelete = async (item) => {
    const confirmDelete = window.confirm('유보금 내역을 삭제할까요?');
    if (!confirmDelete) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/reserve-funds/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingRow?.id === item.id) {
        setEditingRow(null);
      }
      setSuccess('유보금 내역이 삭제되었습니다.');
      await fetchList();
    } catch (err) {
      logger.apiError('DELETE', '/api/reserve-funds/:id', err);
      setError(err?.response?.data?.error || '유보금 삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const groupedRows = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      if (a.occurred_date === b.occurred_date) {
        return b.id - a.id;
      }
      return a.occurred_date < b.occurred_date ? 1 : -1;
    });
    const groups = new Map();
    sorted.forEach((item) => {
      const key = item.company || '미지정';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    });

    return Array.from(groups.entries()).map(([companyName, rows]) => {
      const totalsByCompany = rows.reduce((acc, row) => {
        if (row.direction === 'in') {
          acc.total_in += Number(row.amount || 0);
        } else {
          acc.total_out += Number(row.amount || 0);
        }
        return acc;
      }, { total_in: 0, total_out: 0 });
      totalsByCompany.net = totalsByCompany.total_in - totalsByCompany.total_out;
      return { companyName, rows, totals: totalsByCompany };
    });
  }, [items]);

  return (
    <div className="reserve-funds-layout">
      <div className="settlement-input-card">
        <div className="reserve-funds-header">
          <div>
            <h2>유보금 내역</h2>
          </div>
          <div className="reserve-funds-actions">
            <button
              type="button"
              className="submit-btn reserve-open-button"
              onClick={() => {
                setError('');
                setSuccess('');
                setIsModalOpen(true);
              }}
            >
              유보금 등록
            </button>
          </div>
        </div>
        <div className="reserve-filter-row">
          <select
            value={filterCompany}
            onChange={(event) => {
              setFilterCompany(event.target.value);
              setListPage(1);
            }}
            className="reserve-filter-input"
          >
            <option value="">전체 분류</option>
            {companyOptions.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
          <select
            value={filterDirection}
            onChange={(event) => {
              setFilterDirection(event.target.value);
              setListPage(1);
            }}
            className="reserve-filter-input"
          >
            <option value="">전체</option>
            <option value="in">입금</option>
            <option value="out">출금</option>
          </select>
          <input
            type="date"
            value={filterDateFrom}
            max={filterDateTo || undefined}
            onChange={(event) => {
              setFilterDateFrom(event.target.value);
              setListPage(1);
            }}
            className="reserve-filter-input"
            title="시작일"
          />
          <span className="reserve-filter-date-sep">~</span>
          <input
            type="date"
            value={filterDateTo}
            min={filterDateFrom || undefined}
            onChange={(event) => {
              setFilterDateTo(event.target.value);
              setListPage(1);
            }}
            className="reserve-filter-input"
            title="종료일"
          />
          <button
            type="button"
            className="reserve-filter-reset"
            onClick={() => {
              setFilterCompany('');
              setFilterDirection('');
              setFilterDateFrom('');
              setFilterDateTo('');
              setListPage(1);
            }}
          >
            필터 초기화
          </button>
        </div>
        <div className="reserve-funds-totals">
          <span>
            {filterDateFrom || filterDateTo
              ? `기간 ${filterDateFrom || '처음'} ~ ${filterDateTo || '오늘'}`
              : '전체 기간'}
          </span>
          <span className="reserve-total-in">입금 {formatCurrency(totals.total_in)}</span>
          <span className="reserve-total-out">출금 {formatCurrency(totals.total_out)}</span>
          <span className="reserve-total-net">잔액 {formatCurrency(totals.net)}</span>
        </div>

        {loadingList ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>불러오는 중...</div>
        ) : listError ? (
          <div className="error" style={{ marginTop: 0 }}>
            <h3>오류</h3>
            <p>{listError}</p>
          </div>
        ) : groupedRows.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>내역이 없습니다.</div>
        ) : (
          <div className="reserve-funds-groups">
            {groupedRows.map((group) => (
              <div key={group.companyName} className="reserve-funds-group">
                <div className="reserve-funds-group-header">
                  <span className="daily-company-label">분류</span>
                  <h3 className="daily-company-title">{group.companyName}</h3>
                </div>
                <table className="deposit-table">
                  <thead>
                    <tr>
                      <th>일자</th>
                      <th>구분</th>
                      <th>금액</th>
                      <th>메모</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((item) => {
                      const isEditing = editingRow?.id === item.id;
                      return (
                        <tr key={item.id}>
                          <td>
                            {isEditing ? (
                              <input
                                type="date"
                                value={editingRow.occurredDate}
                                onChange={(event) => handleEditChange('occurredDate', event.target.value)}
                                className="settlement-inline-input"
                              />
                            ) : (
                              item.occurred_date
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <select
                                value={editingRow.direction}
                                onChange={(event) => handleEditChange('direction', event.target.value)}
                                className="settlement-inline-input"
                              >
                                <option value="in">입금</option>
                                <option value="out">출금</option>
                              </select>
                            ) : (
                              <span className={`reserve-direction ${item.direction}`}>
                                {item.direction === 'in' ? '입금' : '출금'}
                              </span>
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editingRow.amount}
                                onChange={(event) => handleEditChange('amount', event.target.value)}
                                className="settlement-inline-input"
                              />
                            ) : (
                              formatCurrency(item.amount)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingRow.note}
                                onChange={(event) => handleEditChange('note', event.target.value)}
                                className="settlement-inline-input"
                              />
                            ) : (
                              item.note || '-'
                            )}
                          </td>
                          <td className="monthly-fee-actions">
                            {isEditing ? (
                              <div className="settlement-edit-actions">
                                <button
                                  type="button"
                                  onClick={handleSaveEdit}
                                  className="settlement-save-button"
                                  disabled={submitting}
                                >
                                  저장
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="settlement-cancel-button"
                                  disabled={submitting}
                                >
                                  취소
                                </button>
                              </div>
                            ) : (
                              <div className="settlement-edit-actions">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(item)}
                                  className="settlement-edit-button"
                                >
                                  수정
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item)}
                                  className="settlement-delete-button"
                                >
                                  삭제
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="reserve-summary-row">
                      <td>총계</td>
                      <td>
                        <div className="reserve-summary-stack">
                          <span className="reserve-summary-label">입금</span>
                          <span className="reserve-summary-value">{formatCurrency(group.totals.total_in)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="reserve-summary-stack">
                          <span className="reserve-summary-label">출금</span>
                          <span className="reserve-summary-value">{formatCurrency(group.totals.total_out)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="reserve-summary-stack">
                          <span className="reserve-summary-label">순액</span>
                          <span className="reserve-summary-value">{formatCurrency(group.totals.net)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="reserve-summary-count">총 {formatNumber(group.rows.length)}건</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>
        )}

        {listTotalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.max(1, prev - 1))}
              disabled={listPage <= 1}
            >
              이전
            </button>
            <button type="button" disabled>
              {listPage} / {listTotalPages}
            </button>
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.min(listTotalPages, prev + 1))}
              disabled={listPage >= listTotalPages}
            >
              다음
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>유보금 등록</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="reserveDate">일자</label>
                <input
                  id="reserveDate"
                  type="date"
                  value={occurredDate}
                  onChange={(event) => setOccurredDate(event.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="reserveCompany">분류</label>
                <select
                  id="reserveCompany"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  disabled={loadingCompanies}
                >
                  <option value="">분류 선택</option>
                  {companyOptions.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="reserveDirection">구분</label>
                <select
                  id="reserveDirection"
                  value={direction}
                  onChange={(event) => setDirection(event.target.value)}
                >
                  <option value="in">입금</option>
                  <option value="out">출금</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="reserveAmount">금액</label>
                <input
                  id="reserveAmount"
                  type="number"
                  min="0"
                  step="1"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="금액을 입력하세요"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="reserveNote">메모</label>
                <input
                  id="reserveNote"
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="메모 (선택)"
                />
              </div>
              {error && (
                <div className="error">
                  <h3>오류</h3>
                  <p>{error}</p>
                </div>
              )}
              {success && (
                <div className="result">
                  <h3>완료</h3>
                  <p>{success}</p>
                </div>
              )}
              <div className="modal-actions">
                <button className="submit-btn" type="submit" disabled={submitting}>
                  {submitting ? '저장 중...' : '유보금 저장'}
                </button>
                <button
                  type="button"
                  className="settlement-cancel-button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={submitting}
                >
                  닫기
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReserveFundsManagement;
